import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import {
  Mail,
  AlertCircle,
  ExternalLink,
  Copy,
  Check,
  X,
  RefreshCw
} from 'lucide-react';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

function PendingEmailsWidget({ onRefresh }) {
  const [pendingEmails, setPendingEmails] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    loadPendingEmails();
  }, []);

  const loadPendingEmails = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/api/users/management/email-requests/pending`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPendingEmails(response.data.data);
    } catch (error) {
      console.error('Erro ao carregar emails pendentes:', error);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copiado para área de transferência');
  };

  const handleRefresh = () => {
    loadPendingEmails();
    if (onRefresh) onRefresh();
  };

  if (pendingEmails.length === 0) {
    return null;
  }

  return (
    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-3 flex-1">
          <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <h3 className="font-semibold text-yellow-900 mb-1">
              📧 {pendingEmails.length} Email{pendingEmails.length > 1 ? 's' : ''} Pendente{pendingEmails.length > 1 ? 's' : ''} de Criação
            </h3>
            <p className="text-sm text-yellow-800 mb-3">
              Os seguintes emails precisam ser criados no Hostinger:
            </p>

            {!expanded && (
              <button
                onClick={() => setExpanded(true)}
                className="text-sm text-yellow-700 hover:text-yellow-900 underline"
              >
                Ver detalhes
              </button>
            )}
          </div>

          <button
            onClick={handleRefresh}
            className="text-yellow-600 hover:text-yellow-800 p-1"
            title="Atualizar"
          >
            <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {expanded && (
          <button
            onClick={() => setExpanded(false)}
            className="text-yellow-600 hover:text-yellow-800 ml-2"
          >
            <X className="w-5 h-5" />
          </button>
        )}
      </div>

      {expanded && (
        <div className="mt-4 space-y-4">
          {pendingEmails.map((request) => (
            <div
              key={request.id}
              className="bg-white border border-yellow-300 rounded-lg p-4"
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <h4 className="font-medium text-gray-900">
                    {request.user_name}
                  </h4>
                  <p className="text-sm text-gray-600">
                    Solicitado por {request.requested_by_name} em{' '}
                    {new Date(request.requested_at).toLocaleString('pt-BR')}
                  </p>
                </div>
              </div>

              {/* Email */}
              <div className="mb-3">
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Email a Criar:
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={request.requested_email}
                    readOnly
                    className="flex-1 px-3 py-2 text-sm border rounded-lg bg-gray-50"
                  />
                  <button
                    onClick={() => copyToClipboard(request.requested_email)}
                    className="px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg"
                    title="Copiar email"
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Password */}
              <div className="mb-4">
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Senha Sugerida:
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={request.suggested_password}
                    readOnly
                    className="flex-1 px-3 py-2 text-sm border rounded-lg bg-gray-50 font-mono"
                  />
                  <button
                    onClick={() => copyToClipboard(request.suggested_password)}
                    className="px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg"
                    title="Copiar senha"
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Instructions */}
              <div className="bg-blue-50 border border-blue-200 rounded p-3 mb-3">
                <p className="text-xs font-medium text-blue-900 mb-2">
                  📋 Passos para criar no Hostinger:
                </p>
                <ol className="text-xs text-blue-800 space-y-1">
                  <li>1. Acesse o painel do Hostinger</li>
                  <li>2. Vá em "Emails" → "Contas de Email"</li>
                  <li>3. Criar conta com o email acima</li>
                  <li>4. Usar a senha sugerida</li>
                  <li>5. Voltar aqui e confirmar criação</li>
                </ol>
              </div>

              {/* Actions */}
              <div className="flex gap-2">
                <a
                  href="https://hpanel.hostinger.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white text-sm rounded-lg hover:bg-blue-600"
                >
                  <ExternalLink className="w-4 h-4" />
                  Abrir Hostinger
                </a>
                <button
                  onClick={handleRefresh}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 text-sm rounded-lg hover:bg-gray-200"
                >
                  <RefreshCw className="w-4 h-4" />
                  Atualizar Lista
                </button>
              </div>
            </div>
          ))}

          <div className="bg-green-50 border border-green-200 rounded p-3">
            <p className="text-xs text-green-800">
              💡 <strong>Dica:</strong> Após criar os emails no Hostinger, volte à lista de usuários
              e clique no botão "Pendente" ao lado de cada email para confirmá-lo como criado.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

export default PendingEmailsWidget;
